// ============================================================================
//  HOUSE BOT PROMPT - Ask player if they want to play vs bots (up to 3)

// ============================================================================
import React, { useState } from 'react';
import { houseBotService } from '../../services/houseBotService';

interface HouseBotPromptProps {
  tableId: string;
  currency: string;
  playerCount: number;
  hasActiveBot: boolean;
  botCount: number;
  onBotSummoned?: () => void;
  onReadyUp?: () => void;
  onDismissed?: () => void;
}

const HouseBotPrompt: React.FC<HouseBotPromptProps> = ({
  tableId, currency, playerCount, hasActiveBot, botCount, onBotSummoned, onReadyUp, onDismissed,
}) => {
  const [loading, setLoading] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Only show on PlayCoin tables when solo with no active game
  if (currency === 'pkcash' || playerCount !== 1 || dismissed) return null;

  const maxBots = 2;
  const canAddMore = botCount < maxBots;

  const handleSummon = async () => {
    setLoading(true);
    setError(null);
    try {
      if (hasActiveBot && !canAddMore) {
        // All bots present - ready up to start countdown
        onReadyUp?.();
      } else if (hasActiveBot && botCount > 0 && canAddMore) {
        // Add another bot
        const difficulty = Math.random() > 0.5 ? 'hard' : 'medium';
        const result = await houseBotService.summonBot(tableId, difficulty);
        if (result.success) {
          onBotSummoned?.();
        } else {
          setError(result.error || 'Failed to add bot');
        }
      } else if (hasActiveBot) {
        // Bot already pre-seeded - ready up to start countdown
        onReadyUp?.();
      } else {
        // No bot yet - summon first one
        const difficulty = Math.random() > 0.5 ? 'hard' : 'medium';
        const result = await houseBotService.summonBot(tableId, difficulty);
        if (result.success) {
          onBotSummoned?.();
        } else {
          setError(result.error || 'Failed to summon bot');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleStartPlaying = async () => {
    setLoading(true);
    try {
      onReadyUp?.();
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    setDismissing(true);
    if (hasActiveBot) {
      try {
        await houseBotService.dismissBot(tableId, true);
        console.log('🤖 [HouseBotPrompt] Bots dismissed from table');
      } catch (err) {
        console.warn('⚠️ [HouseBotPrompt] Failed to dismiss bots:', err);
      }
    }
    setDismissed(true);
    setDismissing(false);
    onDismissed?.();
  };

  const getTitle = () => {
    if (botCount >= maxBots) return 'Table is full!';
    if (botCount > 0) return `${botCount} Bot${botCount > 1 ? 's' : ''} at the table`;
    if (hasActiveBot) return 'House Bot is ready!';
    return 'Waiting for players...';
  };

  const getDescription = () => {
    if (botCount >= maxBots) return 'Max bots reached. Ready to play?';
    if (botCount > 0) return `Add up to ${maxBots - botCount} more bot${maxBots - botCount > 1 ? 's' : ''} or start playing!`;
    if (hasActiveBot) return 'A House Bot is at the table. Ready to play?';
    return 'Practice against House Bots while you wait!';
  };

  return (
    <div style={{
      position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      background: 'rgba(0,0,0,0.85)', borderRadius: '16px', padding: '20px',
      textAlign: 'center', zIndex: 200, minWidth: '260px', maxWidth: '320px',
      width: 'calc(100vw - 48px)',
      border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
    }}>
      <div style={{ fontSize: '32px', marginBottom: '6px' }}></div>

      <h3 style={{ color: '#fff', margin: '0 0 4px 0', fontSize: '17px' }}>
        {getTitle()}
      </h3>
      <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 6px 0', fontSize: '13px', lineHeight: '1.3' }}>
        {getDescription()}
      </p>
      <p style={{ color: 'rgba(6, 182, 212, 0.85)', margin: '0 0 12px 0', fontSize: '11px', lineHeight: '1.3', fontStyle: 'italic' }}>
        Bots are trained to play like real players. They see only their own cards and use the same shuffled deck - no peeking.
      </p>

      {/* Bot count indicator */}
      {botCount > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '12px',
        }}>
          {Array.from({ length: maxBots }, (_, idx) => idx + 1).map(i => (
            <div key={i} style={{
              width: '10px', height: '10px', borderRadius: '50%',
              background: i <= botCount ? '#06b6d4' : 'rgba(255,255,255,0.15)',
              transition: 'background 0.3s',
            }} />
          ))}
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginLeft: '4px' }}>
            {botCount}/{maxBots}
          </span>
        </div>
      )}

      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)',
          borderRadius: '8px', padding: '8px 12px', marginBottom: '10px',
          color: '#fca5a5', fontSize: '12px',
        }}>{error}</div>
      )}

      {/* Action buttons */}
      {botCount > 0 && canAddMore ? (
        // Show both "Add Bot" and "Start Playing" side by side
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <button onClick={handleSummon} disabled={loading || dismissing} style={{
            flex: 1, padding: '12px 8px', borderRadius: '10px', border: 'none',
            background: loading ? 'rgba(255,255,255,0.1)' : 'rgba(6, 182, 212, 0.2)',
            color: loading ? 'rgba(255,255,255,0.3)' : '#06b6d4',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px', fontWeight: 700, transition: 'all 0.2s',
            border: '1px solid rgba(6, 182, 212, 0.3)',
          } as any}>
            {loading ? '⏳' : `+ Bot`}
          </button>
          <button onClick={handleStartPlaying} disabled={loading || dismissing} style={{
            flex: 1.5, padding: '12px 8px', borderRadius: '10px', border: 'none',
            background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
            color: '#fff',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px', fontWeight: 700, transition: 'all 0.2s',
            boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)',
          }}>
            ▶ Start Playing
          </button>
        </div>
      ) : botCount >= maxBots ? (
        // Max bots - only start button
        <button onClick={handleStartPlaying} disabled={loading || dismissing} style={{
          width: '100%', padding: '13px 24px', borderRadius: '12px', border: 'none',
          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
          color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '15px', fontWeight: 700, transition: 'all 0.2s',
          boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)',
          marginBottom: '8px',
        }}>
          ▶ Start Playing
        </button>
      ) : (
        // No bots yet - summon first one
        <button onClick={handleSummon} disabled={loading || dismissing} style={{
          width: '100%', padding: '13px 24px', borderRadius: '12px', border: 'none',
          background: loading
            ? 'rgba(255,255,255,0.1)'
            : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
          color: loading ? 'rgba(255,255,255,0.3)' : '#fff',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '15px', fontWeight: 700, transition: 'all 0.2s',
          boxShadow: loading ? 'none' : '0 4px 15px rgba(6, 182, 212, 0.3)',
          marginBottom: '8px',
        }}>
          {loading ? '⏳ Starting...' : ' Play vs House Bot'}

        </button>
      )}

      <button onClick={handleDismiss} disabled={loading || dismissing} style={{
        background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)',
        cursor: 'pointer', fontSize: '12px', padding: '4px 8px',
      }}>{dismissing ? 'Removing bots...' : 'No thanks, I\'ll wait'}</button>
    </div>
  );
};

export default HouseBotPrompt;
